import React from 'react';
import { Lock, EyeOff, Heart, Image as ImageIcon, Check, CreditCard } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { EscortProfile } from '@/types';

interface ProfilePrivateContentProps {
  profile: EscortProfile;
  hasAccess: boolean;
  onViewPrivateContent: () => void;
}

// Composant Placeholder pour une image privée
const PrivateMediaPlaceholder: React.FC = () => (
  <div className="aspect-square bg-pink-100 rounded-md flex items-center justify-center">
    <EyeOff className="h-8 w-8 text-pink-300" />
  </div>
);

// Composant Placeholder pour un service premium
const PremiumServicePlaceholder: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-md filter blur-sm">
    <span className="text-gray-600">{text}</span>
    <span className="text-pink-400 font-medium flex items-center">
      <span className="mr-1">XX</span> € <Lock className="h-3 w-3 ml-1"/>
    </span>
  </div>
);

const ProfilePrivateContent: React.FC<ProfilePrivateContentProps> = ({ 
  profile,
  hasAccess,
  onViewPrivateContent 
}) => {
  const { t } = useTranslation(['common', 'profile']);

  // --- Clés de traduction (mises à jour et nouvelles) ---
  const premiumAccessTitle = t('profile:premium_access', 'Abonnement Premium');
  const exclusiveContentText = t('profile:access_exclusive_content_for', 'Accédez à tout le contenu exclusif de {name}', { name: profile.displayName || 'cette escort' });
  const pricePerMonth = t('profile:price_per_month', '{price}€ / mois', { price: profile.fanSubscriptionPrice || 5 });
  const photosVideosFeature = t('profile:private_photos_videos', 'Photos et vidéos exclusives');
  const privateSecretsFeature = t('profile:private_secrets', 'Ses petits secrets');
  const premiumServicesOfferedFeature = t('profile:premium_services_offered', 'Services premium proposés');
  const priorityMessagesFeature = t('profile:priority_messages', 'Messages prioritaires');
  const subscribeNowButton = t('profile:subscribe_now', "S'abonner maintenant");
  const securePaymentText = t('profile:secure_payment_cancel_anytime', 'Paiement sécurisé. Annulez à tout moment.');
  const privateMediaTitle = t('profile:private_media', 'Médias privés');
  const premiumServicesTitle = t('profile:premium_services', 'Services premium');
  const moreServicesText = t('profile:more_services', '+ {count} autres services', { count: 3 });
  const servicePlaceholder1 = t('profile:service_placeholder_1', 'Service exclusif A');
  const servicePlaceholder2 = t('profile:service_placeholder_2', 'Vidéo personnalisée');
  const servicePlaceholder3 = t('profile:service_placeholder_3', 'Accès groupe privé');

  return (
    <div className="space-y-6">
      {/* --- Carte Abonnement --- */}
      <Card className="bg-white shadow-md overflow-hidden">
        <CardContent className="p-6 text-center">
          <Lock className="h-10 w-10 mx-auto text-pink-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-1">
            {t('profile.private_content_locked', 'Contenu privé verrouillé')}
          </h2>
          <p className="text-sm text-gray-500 mb-6">
            {t('profile.private_content_subscription_needed', 'Abonnez-vous pour débloquer le contenu exclusif')}
          </p>

          {/* Bloc intérieur pour les détails de l'abonnement */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-left mb-6 max-w-md mx-auto">
            <h3 className="text-lg font-medium text-pink-600 mb-1">{premiumAccessTitle}</h3>
            <p className="text-sm text-gray-600 mb-2">{exclusiveContentText}</p>
            <p className="text-2xl font-bold text-pink-500 mb-4">{pricePerMonth}</p>
            <ul className="space-y-2 text-sm text-gray-700 mb-5">
              <li className="flex items-center">
                <Check className="h-4 w-4 mr-2 text-green-500 flex-shrink-0" />
                {photosVideosFeature}
              </li>
              <li className="flex items-center">
                <Check className="h-4 w-4 mr-2 text-green-500 flex-shrink-0" />
                {privateSecretsFeature}
              </li>
              <li className="flex items-center">
                <Check className="h-4 w-4 mr-2 text-green-500 flex-shrink-0" />
                {premiumServicesOfferedFeature}
              </li>
              <li className="flex items-center"> {/* Nouvelle feature */}
                <Check className="h-4 w-4 mr-2 text-green-500 flex-shrink-0" />
                {priorityMessagesFeature}
              </li>
            </ul>
            <Button 
              onClick={onViewPrivateContent}
              className="w-full bg-pink-600 hover:bg-pink-700 text-white"
              size="lg"
            >
              <CreditCard className="h-4 w-4 mr-2" /> {/* Icône mise à jour */}
              {subscribeNowButton}
            </Button>
          </div>
          <p className="text-xs text-gray-500">{securePaymentText}</p>
        </CardContent>
      </Card>

      {/* --- Carte Médias Privés --- */}
      <Card className="bg-white shadow-md">
        <CardHeader className="border-b border-gray-200 p-4"> {/* Ajustement padding */} 
          <CardTitle className="text-base font-medium text-gray-700 flex items-center">
            <ImageIcon className="h-5 w-5 mr-2 text-pink-500" /> {/* Icône mise à jour */} 
            {privateMediaTitle}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4"> {/* Ajustement padding */} 
          <div className="grid grid-cols-3 gap-2"> {/* Grille d'images */}
            <PrivateMediaPlaceholder />
            <PrivateMediaPlaceholder />
            <PrivateMediaPlaceholder />
            <PrivateMediaPlaceholder />
            <PrivateMediaPlaceholder />
            <PrivateMediaPlaceholder />
          </div>
        </CardContent>
      </Card>

      {/* --- Carte Services Premium --- */}
      <Card className="bg-white shadow-md">
        <CardHeader className="border-b border-gray-200 p-4"> {/* Ajustement padding */} 
          <CardTitle className="text-base font-medium text-gray-700 flex items-center">
            <Heart className="h-5 w-5 mr-2 text-pink-500" /> {/* Icône mise à jour */} 
            {premiumServicesTitle}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-2"> {/* Ajustement padding et ajout space-y */} 
          {/* Liste de services floutés */}
          <PremiumServicePlaceholder text={servicePlaceholder1} />
          <PremiumServicePlaceholder text={servicePlaceholder2} />
          <PremiumServicePlaceholder text={servicePlaceholder3} />
          {/* Texte "+ autres services" */}
          <p className="text-sm text-pink-500 text-center pt-2"> 
            {moreServicesText}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfilePrivateContent;
